<!--
// nuxt-ui/components/pages/region-tables-kwcc/TableKwccProbability.vue
-->
<script setup lang="ts">
import type { TablesKwccData } from "~/src/options/domain/models/CmsOptions";
import type { Team } from "~/src/team/domain/models/Team";
import { setAliasUrl, slugify } from "~/src/Shared/utils";

type Props = {
  tableData: TablesKwccData[];
  teamsObj: Record<number, Team>;
  lastUpdated: string;
  firstColHeader: string;
  secondColHeader: string;
  showSecondAndTotalCol?: boolean;
  isHomeTable?: boolean;
  isInWorldCupTables?: boolean;
  isQueens?: boolean;
};

const props = defineProps<Props>();

const localePath = useLocalePath();
</script>

<template>
  <div :class="[{ 'small-table w-full': isHomeTable }, { 'queens-table w-full': isQueens }]">
    <p
      v-if="lastUpdated && !isHomeTable"
      :class="[isInWorldCupTables ? 'text-center text-sm' : 'text-right']"
    >
      {{ $t("tables.lastUpdate") }}: {{ lastUpdated }}
    </p>
    <div
      v-if="tableData.length"
      id="standingDesktop"
      :class="[
        'bg-gray-900 p-2 rounded-lg my-3',
        { 'relative overflow-x-scroll hide-scrollbar': !isHomeTable },
      ]"
    >
      <div
        :class="[
          isHomeTable ? 'min-w-full' : 'overflow-x-scroll hide-scrollbar ',
          { '!max-w-min': isInWorldCupTables },
        ]"
      >
        <div class="header-table-standing">
          <div class="header-standing-team">
            <span class="text-left">{{ $t("tables.position.ab") }}</span>
            <span class="col-start-2 col-span-3 pl-4">{{ $t("tables.team.word") }}</span>
          </div>
          <div :class="['header-standing-data', showSecondAndTotalCol ? 'grid-cols-4' : 'grid-cols-2']">
            <span class="header-title-item">{{ "prob. (%)" }}</span>
            <span class="header-title-item">{{ firstColHeader }}</span>
            <span v-if="showSecondAndTotalCol && !isHomeTable" class="header-title-item">
              {{ secondColHeader }}
            </span>
            <span v-if="showSecondAndTotalCol && !isHomeTable" class="header-title-item">{{ "total" }}</span>
          </div>
        </div>
        <div v-for="(team, i) in tableData" :key="team.team_id" class="standing-row relative min-w-max">
          <div class="standing-row-left">
            <span :class="['split-standing', { '!bg-red-500': team.pre_qualified }]"></span>
            <div class="standing-row-rank-team">
              <div v-if="!team.reason" class="standing-rank">
                <p>{{ team.ranking }}</p>
              </div>

              <div class="standing-team-container">
                <div class="logo-team-container">
                  <NuxtImg
                    :src="setAliasUrl(teamsObj[team.team_id].logo.url)"
                    :alt="teamsObj[team.team_id].name"
                    class="object-contain"
                    :loading="i > 6 ? 'lazy' : 'eager'"
                    width="44"
                    height="44"
                  />
                </div>
                <div :class="{ 'hidden md:block': !isHomeTable }">
                  <nuxt-link
                    :to="
                      localePath({
                        name: 'teams-team-teamName',
                        params: {
                          team: teamsObj[team.team_id].id,
                          teamName: slugify(teamsObj[team.team_id].name),
                        },
                      })
                    "
                  >
                    <h2>
                      {{ isHomeTable ? teamsObj[team.team_id].shortName : teamsObj[team.team_id].name }}
                    </h2>
                  </nuxt-link>
                </div>
                <div v-if="!isHomeTable" class="block md:hidden">
                  <nuxt-link
                    :to="
                      localePath({
                        name: 'teams-team-teamName',
                        params: {
                          team: teamsObj[team.team_id].id,
                          teamName: slugify(teamsObj[team.team_id].name),
                        },
                      })
                    "
                  >
                    {{ teamsObj[team.team_id].shortName }}
                  </nuxt-link>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!team.reason"
            :class="['standing-row-data', showSecondAndTotalCol ? 'grid-cols-4' : 'grid-cols-2']"
          >
            <span class="standing-table-item-data !text-white">{{ team.probability }}</span>
            <span class="standing-table-item-data">
              {{ isHomeTable ? team.pts_col_1 + team.pts_col_2 : team.pts_col_1 }}
            </span>
            <span v-if="showSecondAndTotalCol && !isHomeTable" class="standing-table-item-data">{{
              team.pts_col_2
            }}</span>
            <span v-if="showSecondAndTotalCol && !isHomeTable" class="standing-table-item-data">
              {{ team.pts_col_1 + team.pts_col_2 }}
            </span>
          </div>
          <div v-else :class="['standing-row-data', showSecondAndTotalCol ? 'grid-cols-4' : 'grid-cols-2']">
            <span
              :class="[
                'standing-table-item-data !justify-start md:!justify-end uppercase ',
                showSecondAndTotalCol ? '!col-span-4' : '!col-span-2',
                isHomeTable ? 'min-w-1 !text-[10px]' : '!min-w-[240px] md:!text-base',
              ]"
            >
              {{ team.reason }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-table-standing {
  @apply flex justify-start items-center text-xs text-gray-400 uppercase mb-2 min-w-max;
}

.small-table .header-table-standing {
  @apply text-[10px];
}

.standing-row {
  @apply flex justify-start items-center relative bg-gray-800 h-16 mb-1 rounded-lg;
}

.small-table .standing-row {
  @apply !h-11;
}

.standing-row-left {
  @apply rounded-l-lg overflow-hidden bg-gray-800 sticky left-0 w-[170px] md:w-auto h-16 flex items-center;
}

.small-table .standing-row-left {
  @apply !w-auto max-w-min h-11 !static;
}

.standing-row-data {
  @apply rounded-r-lg overflow-hidden bg-gray-800 h-16;
}

.small-table .standing-row-data {
  @apply h-11;
}

.standing-row-rank-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 pl-3 md:pl-4 md:text-lg font-bold w-[160px] md:w-[362px] md:min-w-[362px] md:max-w-[450px];
}

.small-table .standing-row-rank-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[130px] md:w-[130px] md:min-w-[130px] md:max-w-max pl-1 md:pl-1 static text-sm;
}

.queens-table .standing-row-rank-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[160px] md:w-[270px] md:min-w-[270px] md:max-w-max pl-1 md:pl-1 static md:text-lg font-bold;
}

.header-standing-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 pl-3 md:pl-4 w-[170px] md:w-[362px] md:min-w-[362px] md:max-w-[450px] sticky left-0 bg-gray-900;
}

.small-table .header-standing-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[130px] md:w-[130px] md:min-w-[130px] md:max-w-max pl-1 md:pl-1 !static;
}

.queens-table .header-standing-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[170px] md:w-[270px] md:min-w-[270px] md:max-w-max pl-1 md:pl-1 !static;
}

.standing-rank {
  @apply h-full w-full flex items-center justify-start text-left;
}

span.split-standing {
  @apply h-16 w-[4px] bg-gray-800;
}

.small-table span.split-standing {
  @apply h-11;
}

.standing-team-container {
  @apply col-start-2 col-span-3 text-left flex justify-start gap-3 md:gap-4 items-center h-full;
}

.small-table .standing-team-container {
  @apply max-w-min md:gap-2;
}

.logo-team-container {
  @apply w-11 h-11 flex justify-center items-center;
}

.small-table .logo-team-container {
  @apply !w-9 h-9;
}

.standing-table-item-data {
  @apply text-center h-full flex justify-center items-center md:text-lg text-gray-400 font-bold min-w-[60px];
}

.small-table .standing-table-item-data {
  @apply text-sm md:text-sm min-w-[48px];
}

.small-table .standing-table-item-data {
  @apply text-base;
}

.header-title-item {
  @apply justify-center flex;
}

.header-standing-data,
.standing-row-data {
  @apply grid gap-x-7 ml-auto text-center pr-5 min-w-max;
}

.small-table .header-standing-data,
.small-table .standing-row-data {
  @apply gap-x-2 ml-auto pr-2 min-w-1;
}
</style>
